import { useEffect, useState, type RefObject } from 'react';
import { HIGHLIGHT_SWATCHES, type SelectionPiece } from '../services/highlights';
import type { HighlightColor } from '../types';

interface HighlightToolbarProps {
  // The element holding the verses; only selections inside it get the toolbar.
  containerRef: RefObject<HTMLElement>;
  onApply: (pieces: SelectionPiece[], color: HighlightColor | null) => void;
}

interface ToolbarState {
  pieces: SelectionPiece[];
  top: number;
  left: number;
}

const TOOLBAR_HEIGHT = 40;
const TOOLBAR_HALF_WIDTH = 150;

/** The number of characters of `el`'s text that come before (node, offset). */
function offsetWithin(el: HTMLElement, node: Node, offset: number): number {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.setEnd(node, offset);
  return range.toString().length;
}

/**
 * Cuts a DOM selection into one piece per verse it touches. Each verse's text is rendered in an element carrying
 * data-verse-number whose text content is exactly the verse text, so text offsets inside it are verse offsets.
 */
function readSelectionPieces(container: HTMLElement, range: Range): SelectionPiece[] {
  const pieces: SelectionPiece[] = [];
  container.querySelectorAll<HTMLElement>('[data-verse-number]').forEach((el) => {
    if (!range.intersectsNode(el)) return;
    const length = el.textContent?.length ?? 0;
    const start = el.contains(range.startContainer) ? offsetWithin(el, range.startContainer, range.startOffset) : 0;
    const end = el.contains(range.endContainer) ? offsetWithin(el, range.endContainer, range.endOffset) : length;
    if (end > start) {
      pieces.push({ verseNumber: Number(el.dataset.verseNumber), start, end });
    }
  });
  return pieces;
}

/**
 * A row of colours that appears under selected verse text. Picking one paints the selection; the last button erases
 * any highlight in it.
 */
export default function HighlightToolbar({ containerRef, onApply }: HighlightToolbarProps) {
  const [state, setState] = useState<ToolbarState | null>(null);

  useEffect(() => {
    const update = () => {
      const container = containerRef.current;
      const selection = window.getSelection();
      if (!container || !selection || selection.isCollapsed || selection.rangeCount === 0) {
        setState(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const pieces = container.contains(range.commonAncestorContainer) ? readSelectionPieces(container, range) : [];
      if (pieces.length === 0) {
        setState(null);
        return;
      }

      // Below the selection, which also keeps clear of the native selection menu phones show above it; above it
      // only when there is no room left underneath.
      const rect = range.getBoundingClientRect();
      const top = rect.bottom + 8 + TOOLBAR_HEIGHT > window.innerHeight
        ? rect.top - 8 - TOOLBAR_HEIGHT
        : rect.bottom + 8;
      const left = Math.min(
        Math.max(rect.left + rect.width / 2, TOOLBAR_HALF_WIDTH + 8),
        window.innerWidth - TOOLBAR_HALF_WIDTH - 8,
      );
      setState({ pieces, top, left });
    };

    document.addEventListener('selectionchange', update);
    // The toolbar is fixed to the viewport, so it has to follow the selection when the verse list scrolls.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      document.removeEventListener('selectionchange', update);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [containerRef]);

  if (!state) return null;

  const apply = (color: HighlightColor | null) => {
    onApply(state.pieces, color);
    window.getSelection()?.removeAllRanges();
    setState(null);
  };

  // Acting on pointerdown, and cancelling its default, runs before the press could collapse the selection (and so
  // unmount the toolbar). Keyboard activation arrives as a click with detail 0.
  const actionProps = (color: HighlightColor | null) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      apply(color);
    },
    onClick: (e: React.MouseEvent) => {
      if (e.detail === 0) apply(color);
    },
  });

  return (
    <div
      role="toolbar"
      aria-label="Highlight selected text"
      style={{ top: state.top, left: state.left }}
      className="fixed z-50 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1.5 rounded-full shadow-lg bg-white border border-gray-200 dark:bg-ink-700 dark:border-ink-500"
    >
      {HIGHLIGHT_SWATCHES.map(({ color, label, swatch }) => (
        <button
          key={color}
          type="button"
          title={`Highlight ${label.toLowerCase()}`}
          aria-label={`Highlight ${label.toLowerCase()}`}
          className={`h-6 w-6 rounded-full border border-black/10 transition-transform hover:scale-110 ${swatch}`}
          {...actionProps(color)}
        />
      ))}
      <span className="w-px h-5 bg-gray-200 dark:bg-ink-500" aria-hidden="true" />
      <button
        type="button"
        title="Remove highlight"
        aria-label="Remove highlight"
        className="h-6 w-6 rounded-full flex items-center justify-center transition-colors text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-parchment-100 dark:hover:bg-ink-600"
        {...actionProps(null)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" d="M5.7 5.7l12.6 12.6" />
        </svg>
      </button>
    </div>
  );
}
